import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Play,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Clock,
  Plus,
  Video,
  Image,
  FileText
} from 'lucide-react';
import { demoPostVideo } from './demoData';

const DemoFeedVideo = ({ userType }) => {
  const [posts] = useState(demoPostVideo);
  const [liked, setLiked] = useState({});
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostType, setNewPostType] = useState('video');

  const handleLike = (postId) => {
    setLiked(prev => ({ ...prev, [postId]: !prev[postId] }));
    console.log(`Demo: ${liked[postId] ? 'Rimosso like' : 'Aggiunto like'} per post ${postId}`);
  };

  const handleShare = (postId) => {
    console.log(`Demo: Condiviso post ${postId}`);
  };

  const handleCreatePost = () => {
    console.log(`Demo: Creazione post ${newPostType} simulata`);
    setShowCreatePost(false);
  };

  const CreatePostModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
        <h3 className="text-lg font-semibold mb-4">Crea nuovo post</h3>
        
        {/* Tipo Post */}
        <div className="mb-6">
          <label className="text-sm text-gray-600 mb-2 block">Tipo di contenuto:</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'video', label: 'Video', icon: Video, color: 'bg-blue-500' },
              { key: 'immagine', label: 'Foto', icon: Image, color: 'bg-green-500' },
              { key: 'testo', label: 'Testo', icon: FileText, color: 'bg-purple-500' }
            ].map((tipo) => {
              const IconComponent = tipo.icon;
              return (
                <Button
                  key={tipo.key}
                  variant={newPostType === tipo.key ? "default" : "outline"}
                  onClick={() => setNewPostType(tipo.key)}
                  className={`flex flex-col items-center gap-2 h-20 ${
                    newPostType === tipo.key ? tipo.color + ' text-white' : 'hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span className="text-xs">{tipo.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">Titolo:</label>
            <input
              type="text"
              placeholder="Inserisci titolo del post..."
              className="w-full mt-1 p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-600">Descrizione:</label>
            <textarea
              placeholder="Racconta la tua storia, condividi un successo, un evento..."
              className="w-full mt-1 p-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          {newPostType === 'video' && (
            <div className="bg-blue-50 p-3 rounded-xl">
              <p className="text-sm text-blue-700">
                📹 <strong>Video Tips:</strong> Condividi storie di successo, tutorial, eventi formativi. 
                I video ottengono 3x più engagement!
              </p>
            </div>
          )}

          {newPostType === 'immagine' && (
            <div className="bg-green-50 p-3 rounded-xl">
              <p className="text-sm text-green-700">
                📸 <strong>Foto Tips:</strong> Team building, nuove sedi, risultati mensili. 
                Le immagini di qualità aumentano la credibilità!
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <Button
            onClick={() => setShowCreatePost(false)}
            variant="outline"
            className="flex-1 rounded-xl"
          >
            Annulla
          </Button>
          <Button
            onClick={handleCreatePost}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
          >
            Pubblica Post
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header e Azioni */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Feed Contenuti</h2>
          <p className="text-gray-600">
            {userType === 'agenzia' 
              ? 'Condividi i tuoi successi con i candidati della tua zona' 
              : 'Scopri contenuti delle agenzie nella tua zona'
            }
          </p>
        </div>
        
        {userType === 'agenzia' && (
          <Button
            onClick={() => setShowCreatePost(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Post
          </Button>
        )}
      </div>

      {/* Feed Posts */}
      <div className="space-y-6">
        {posts.map((post) => (
          <Card key={post.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-0">
              {/* Header Post */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {post.logo}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{post.autore}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{post.timestamp}</span>
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        {post.zona_target}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contenuto Post */}
              <div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.titolo}</h3>
                  <p className="text-gray-700 mb-4">{post.descrizione}</p>
                </div>

                {/* Media Content */}
                {post.tipo === 'video' && (
                  <div className="relative">
                    <img 
                      src={post.thumbnail}
                      alt={post.titolo}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <Button
                        onClick={() => console.log(`Demo: Riproduzione video ${post.id}`)}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full w-16 h-16 backdrop-blur"
                      >
                        <Play className="w-8 h-8 ml-1" />
                      </Button>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {post.durata}
                    </div>
                    <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                      LIVE
                    </div>
                  </div>
                )}

                {post.tipo === 'immagine' && (
                  <img 
                    src={post.immagine}
                    alt={post.titolo}
                    className="w-full h-64 object-cover"
                  />
                )}

                {/* Statistiche e Azioni */}
                <div className="p-4">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-4">
                      {post.views && (
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {post.views} visualizzazioni
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        {post.likes + (liked[post.id] ? 1 : 0)} mi piace
                      </span>
                      {post.commenti && (
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {post.commenti} commenti
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleLike(post.id)}
                      variant="outline"
                      size="sm"
                      className={`flex-1 rounded-xl ${
                        liked[post.id] 
                          ? 'text-red-600 border-red-300 bg-red-50' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${liked[post.id] ? 'fill-current' : ''}`} />
                      Mi piace
                    </Button>
                    
                    <Button
                      onClick={() => console.log(`Demo: Commento post ${post.id}`)}
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-xl hover:bg-gray-50"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Commenta
                    </Button>
                    
                    <Button
                      onClick={() => handleShare(post.id)}
                      variant="outline"
                      size="sm"
                      className="rounded-xl hover:bg-gray-50"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Demo Info */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-4">
        <div className="flex items-start gap-2">
          <Video className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="text-sm text-purple-700">
            <strong>Demo Feed Video:</strong> {userType === 'agenzia' 
              ? 'Come agenzia puoi creare post video, foto e testo per comunicare successi e eventi ai candidati della tua zona. Clicca "Nuovo Post" per provare!'
              : 'Come candidato vedi contenuti mirati delle agenzie nella tua zona. Puoi interagire con like, commenti e condivisioni per mostrare interesse.'
            }
          </div>
        </div>
      </div>

      {/* Modal Create Post */}
      {showCreatePost && <CreatePostModal />}
    </div>
  );
};

export default DemoFeedVideo;